import { FIRST_SHOPIFY_MONEY_AMOUNT_REGEX } from '@/entrypoints/utils';
import { createLogger } from '@/lib/logger';
import {
  Cart,
  CartEvents,
  CartLine,
  enforceGraphQLID,
  graphql,
  TypedCartEvent,
} from '../internals/core';
import { CartEventBus } from '../internals/event-bus';
import {
  LineItemPropertiesUpdateChain,
  type LineItemsDatalayer,
} from '../internals/line-item-properties-update-chain';
import { MissingLineItemKeyError, StaleLineItemKeyError } from '../internals/simple-cart-service';
import { MiniCart } from './mini-cart';

// Line Item Rules
import { remainingAmountRecalculationRule } from '../internals/line-item-rules/remaining-amount-recalculation-rule';
import { promotionLabelQuantityRule } from '../internals/line-item-rules/promotion-label-quantity-rule';
import { promotionMessagingRule } from '../internals/line-item-rules/promotion-messaging-rule';

type LineItemPropertyCssRule = {
  propertyKey: string;
  matchType: 'exact' | 'contains' | string;
  css: string;
};

export type GetProductVariantsResponse = {
  product: {
    variants: {
      nodes: Array<{
        id: string;
        title: string;
        price: {
          amount: string;
          currencyCode: string;
        };
        compareAtPrice: {
          amount: string;
          currencyCode: string;
        } | null;
      }>;
    };
  };
};

class MiniCartLineItemsComponent extends HTMLElement {
  private abortController: AbortController | null = null;
  private eventBus: CartEventBus | null = null;
  private logger = createLogger('mini-cart-line-items');
  private parentMiniCart: MiniCart | null = null;

  private hasRunLineItemPropertiesUpdateChainOnOpen: boolean = false;

  private onMiniCartUIOpened = () => {
    if (!this.parentMiniCart) return;
    if (this.hasRunLineItemPropertiesUpdateChainOnOpen) return;

    const cart = this.parentMiniCart.store.getCart();
    if (!cart) return;

    this.hasRunLineItemPropertiesUpdateChainOnOpen = true;
    void this.runLineItemPropertiesUpdateChain(cart);
  };

  private onMiniCartUIClosed = () => {
    this.hasRunLineItemPropertiesUpdateChainOnOpen = false;
  };

  private lineItemsDatalayerSectionHandle = 'mini-cart-line-items-datalayer';

  private lineItemTemplate: HTMLTemplateElement | null = null;
  private emptyMessageElem: HTMLElement | null = null;
  private itemsListElem: HTMLElement | null = null;
  private reviewsCache: Map<string, string> = new Map();
  private priceAttributeCache: Map<string, { originalPrice: number; compareAtPrice: number }> =
    new Map();
  private priceTextCache: Map<
    string,
    {
      priceText: string;
      compareText: string;
      compareVisible: boolean;
      isFreeProduct: boolean;
    }
  > = new Map();
  private lineItemPropertyCssRules: LineItemPropertyCssRule[] = [];

  lineItemPropertiesUpdateChain = new LineItemPropertiesUpdateChain();
  lineItemPropertiesUpdateChainInFlight: boolean = false;

  /**
   * Quantity deltas the user has clicked but that haven't been sent yet, keyed
   * by line item key. Clicks accumulate here and flush as a single request per
   * line, so spamming +/- costs one round trip instead of one per tap.
   */
  /** Line item elements that already have their click handlers bound. */
  private boundLineItems: WeakSet<Element> = new WeakSet();

  private pendingQuantityDeltas: Map<string, number> = new Map();
  private pendingQuantityFlushTimer: ReturnType<typeof setTimeout> | null = null;
  private static readonly QUANTITY_FLUSH_DELAY_MS = 350;

  private numberFormatter = Intl.NumberFormat(window.Shopify.locale ?? 'en-AU', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  private getProductVariantsQuery: string = `query GetProductVariants($id: ID!) {
  product(id: $id) {
    variants(first: 250) {
      nodes {
        id
        title
        price {
          amount
          currencyCode
        }
        compareAtPrice {
          amount
          currencyCode
        }
      }
    }
  }
}`;

  private parseLineItemsDatalayerFromSectionHtml(html: string): LineItemsDatalayer | null {
    try {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const script = doc.querySelector('#line-items-datalayer');
      const raw = script?.textContent;
      if (!raw) return null;

      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return null;
      if (!('lineItems' in parsed)) return null;
      return parsed as LineItemsDatalayer;
    } catch (e) {
      this.logger.error('Failed to parse line items datalayer section HTML', e);
      return null;
    }
  }

  private async fetchLineItemsDatalayer(): Promise<LineItemsDatalayer> {
    try {
      const url = `${window.Shopify.routes.root}?sections=${encodeURIComponent(this.lineItemsDatalayerSectionHandle)}`;
      const res = await fetch(url, {
        method: 'GET',
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!res.ok) {
        this.logger.error('Failed to fetch line items datalayer section', {
          status: res.status,
        });
        return { lineItems: {} };
      }

      this.logger.info('Fetched line items datalayer section', {
        status: res.status,
      });

      const json = (await res.json()) as Record<string, string>;
      const html = json[this.lineItemsDatalayerSectionHandle];
      if (!html) return { lineItems: {} };

      return this.parseLineItemsDatalayerFromSectionHtml(html) ?? { lineItems: {} };
    } catch (e) {
      this.logger.error('Failed to fetch line items datalayer', e);
      return { lineItems: {} };
    }
  }

  constructor() {
    super();

    this.lineItemPropertiesUpdateChain.addEvent(remainingAmountRecalculationRule());

    this.lineItemPropertiesUpdateChain.addEvent(promotionLabelQuantityRule());
    // this.lineItemPropertiesUpdateChain.addEvent(promotionMessagingRule());

    this.lineItemPropertiesUpdateChain.addEvent(
      LineItemPropertiesUpdateChain.applyProposedPropertiesEvent(),
    );
  }

  private async runLineItemPropertiesUpdateChain(cart: Cart) {
    if (this.lineItemPropertiesUpdateChainInFlight) return;
    if (!this.parentMiniCart) return;
    // Proposals are derived from quantity. Running against a cart the user has
    // already clicked past would write a promotion label that is about to be
    // wrong, so wait for the flush — it emits its own Changed event.
    if (this.pendingQuantityDeltas.size > 0) return;

    this.lineItemPropertiesUpdateChainInFlight = true;
    try {
      const lineItemsDatalayer = await this.fetchLineItemsDatalayer();
      await this.lineItemPropertiesUpdateChain.execute({
        cart,
        cartApi: this.parentMiniCart.cartApi,
        lineItemsDatalayer,
      });
    } catch (e) {
      this.logger.error('Line item properties update chain failed', e);
    } finally {
      this.lineItemPropertiesUpdateChainInFlight = false;
    }
  }

  private readLineItemPropertyCssRulesFromScript(): LineItemPropertyCssRule[] {
    const script = this.querySelector(
      'script[type="application/json"][data-line-item-property-css-rules]',
    ) as HTMLScriptElement | null;
    if (!script) return [];

    const raw = script.textContent;
    if (!raw) return [];

    try {
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];

      return parsed
        .filter((x) => x && typeof x === 'object')
        .map((x) => ({
          propertyKey: typeof (x as any).propertyKey === 'string' ? (x as any).propertyKey : '',
          matchType: typeof (x as any).matchType === 'string' ? (x as any).matchType : 'exact',
          css: typeof (x as any).css === 'string' ? (x as any).css : '',
        }))
        .filter((x) => x.propertyKey.trim() !== '' && x.css.trim() !== '');
    } catch (e) {
      this.logger.error('Failed to parse line item property CSS rules JSON', e);
      return [];
    }
  }

  private initLineItemPropertyCssRules() {
    this.lineItemPropertyCssRules = this.readLineItemPropertyCssRulesFromScript();
  }

  private shouldShowQuantitySelectors(lineItem: CartLine): boolean {
    if (lineItem.vendor.toLowerCase().indexOf('medispa') !== -1) return false;

    return !this.isGiftCard(lineItem);
  }

  /**
   * Gift cards have no meaningful quantity: each line is one card, and for a
   * Gift Card Pro custom amount the quantity encodes the dollar value (12 x
   * $1 = a $12 card), so editing it would silently change the card's value.
   */
  private isGiftCard(lineItem: CartLine): boolean {
    return (
      lineItem.gift_card ||
      lineItem.product_type === 'Gift Card' ||
      Boolean(lineItem.properties?._gcp_custom_value)
    );
  }

  private getPropertyInlineCss(propertyKey: string, rules: LineItemPropertyCssRule[]): string {
    const key = propertyKey.toLowerCase();
    let css = '';

    rules.forEach((rule) => {
      const ruleKey = rule.propertyKey.trim().toLowerCase();
      if (!ruleKey) return;

      const matchType = rule.matchType ?? 'exact';
      const isMatch =
        matchType === 'contains'
          ? key.includes(ruleKey)
          : matchType === 'exact'
            ? key === ruleKey
            : false;

      if (isMatch) css = rule.css;
    });

    return css.trim();
  }

  private isJpyCurrency(): boolean {
    return (
      (window as Window & { Shopify?: { currency?: { active?: string } } }).Shopify?.currency
        ?.active === 'JPY'
    );
  }

  private isAudCurrency(): boolean {
    return (
      (window as Window & { Shopify?: { currency?: { active?: string } } }).Shopify?.currency
        ?.active === 'AUD'
    );
  }

  private formatPrice(price: number) {
    if (this.isJpyCurrency()) {
      return Intl.NumberFormat(window.Shopify?.locale ?? 'en-AU', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(price);
    }
    return this.numberFormatter.format(price);
  }

  private applyCachedPriceText(lineItemElem: HTMLElement, lineKey: string | null | undefined) {
    if (!lineKey) return;
    const cached = this.priceTextCache.get(lineKey);
    if (!cached) return;

    const priceElem = lineItemElem.querySelector('[data-line-item-price]') as HTMLDivElement | null;
    const priceTextElem = lineItemElem.querySelector(
      '[data-line-item-price-text]',
    ) as HTMLSpanElement | null;
    const priceLoaderElem = lineItemElem.querySelector(
      '.mini-cart__item-price-loader',
    ) as HTMLElement | null;
    const comparePriceElem = lineItemElem.querySelector(
      '[data-line-item-compare]',
    ) as HTMLDivElement | null;
    const sideElem = lineItemElem.querySelector('.mini-cart__item-side') as HTMLElement | null;
    const mainElem = lineItemElem.querySelector('.mini-cart__item-main') as HTMLElement | null;

    const hasPriceText = cached.priceText.trim() !== '';
    if (hasPriceText) {
      if (priceTextElem) {
        priceTextElem.innerHTML = cached.priceText;
      } else if (priceElem) {
        priceElem.innerHTML = cached.priceText;
      }
    }

    if (priceElem) priceElem.classList.toggle('is-loaded', hasPriceText);
    priceLoaderElem?.classList.toggle('is-hidden', hasPriceText);

    if (comparePriceElem) {
      comparePriceElem.classList.toggle('is-visible', cached.compareVisible);
      if (cached.compareVisible && cached.compareText.trim() !== '') {
        comparePriceElem.innerHTML = cached.compareText;
      }
    }

    sideElem?.classList.toggle('is-free-product', cached.isFreeProduct);
    mainElem?.classList.toggle('is-free-product', cached.isFreeProduct);
  }

  private capturePriceTextCacheFromExistingDom() {
    if (!this.itemsListElem) return;

    this.itemsListElem.querySelectorAll('[data-line-item]').forEach((lineItemElem) => {
      const lineKey = (
        lineItemElem.querySelector('[data-line-item-key]') as HTMLInputElement | null
      )?.value;
      if (!lineKey) return;

      const priceElem = lineItemElem.querySelector(
        '[data-line-item-price]',
      ) as HTMLDivElement | null;
      const priceTextElem = lineItemElem.querySelector(
        '[data-line-item-price-text]',
      ) as HTMLSpanElement | null;
      const comparePriceElem = lineItemElem.querySelector(
        '[data-line-item-compare]',
      ) as HTMLDivElement | null;
      const sideElem = lineItemElem.querySelector('.mini-cart__item-side') as HTMLElement | null;

      this.priceTextCache.set(lineKey, {
        priceText: (priceTextElem?.textContent ?? priceElem?.textContent ?? '').trim(),
        compareText: comparePriceElem?.textContent?.trim() ?? '',
        compareVisible: comparePriceElem?.classList.contains('is-visible') ?? false,
        isFreeProduct: sideElem?.classList.contains('is-free-product') ?? false,
      });
    });
  }

  private processTitle(title: string | undefined): string {
    if (!title) return '';

    const lastHyphenIndex = title.lastIndexOf(' -');
    let processedTitle =
      lastHyphenIndex !== -1 && !title.includes('CLEARANCE')
        ? title.substring(0, lastHyphenIndex).trim()
        : title.trim();

    processedTitle = processedTitle.replace(/[\u2013\u2014-]+$/, '').trim();

    return processedTitle;
  }

  private addProperty(propertiesElem: HTMLDivElement, text: string, inlineStyle?: string) {
    const propertyDiv = document.createElement('div');
    propertyDiv.textContent = text;
    propertyDiv.classList.add('mini-cart__item-property');
    if (inlineStyle && inlineStyle.trim() !== '') propertyDiv.setAttribute('style', inlineStyle);
    propertiesElem.appendChild(propertyDiv);
  }

  private setupImage(lineItemElem: HTMLElement, lineItem: CartLine) {
    const imageElem = lineItemElem.querySelector('[data-line-item-image]') as HTMLImageElement;
    imageElem.src = lineItem.image ?? '';
    imageElem.alt = lineItem.title ?? '';
  }

  private setupLinks(lineItemElem: HTMLElement, lineItem: CartLine) {
    const linkElems = lineItemElem.querySelectorAll(
      '[data-line-item-url]',
    ) as NodeListOf<HTMLAnchorElement>;
    linkElems.forEach((linkElem) => {
      linkElem.href = lineItem.url ?? '';
    });
  }

  private setupTitle(lineItemElem: HTMLElement, lineItem: CartLine): HTMLAnchorElement {
    const titleElem = lineItemElem.querySelector('[data-line-item-title]') as HTMLAnchorElement;
    titleElem.innerHTML = this.processTitle(lineItem.product_title);
    return titleElem;
  }

  private setupProperties(lineItemElem: HTMLElement, lineItem: CartLine): HTMLDivElement {
    const propertiesElem = lineItemElem.querySelector(
      '[data-line-item-properties]',
    ) as HTMLDivElement;

    Object.entries(lineItem.properties ?? {}).forEach(([key, value]) => {
      if (!value || key.startsWith('_') || key.startsWith('Promotion')) return;
      const inlineCss = this.getPropertyInlineCss(key, this.lineItemPropertyCssRules);
      this.addProperty(propertiesElem, `${key}: ${value}`, inlineCss);
    });

    return propertiesElem;
  }

  private setupVariantOptions(
    lineItem: CartLine,
    propertiesElem: HTMLDivElement,
    titleElem: HTMLAnchorElement,
  ) {
    let extractedMlValue: string | null = null;

    if (lineItem.title) {
      const mlMatch = lineItem.title.match(/(\d+\s*ml)$/i);
      if (mlMatch) {
        extractedMlValue = mlMatch[1];
        this.addProperty(propertiesElem, extractedMlValue);
        const newTitle = lineItem.title
          .substring(0, lineItem.title.length - mlMatch[0].length)
          .trim();
        titleElem.innerHTML = this.processTitle(newTitle);
      }
    }

    lineItem.variant_options?.forEach((option) => {
      if (option !== 'Default Title') {
        const normalizedOption = option.trim().toLowerCase();
        const normalizedMlValue = extractedMlValue?.trim().toLowerCase();

        if (!extractedMlValue || normalizedOption !== normalizedMlValue) {
          this.addProperty(propertiesElem, option);
        }
      }
    });

    titleElem.innerHTML = this.processTitle(titleElem.innerHTML);
  }

  private async computePricing(
    lineItemElem: HTMLElement,
    lineItem: CartLine,
  ): Promise<{
    priceText: string;
    compareText: string;
    compareVisible: boolean;
    isFreeProduct: boolean;
    originalPriceCents: number;
    compareAtPriceCents: number;
  }> {
    const priceElem = lineItemElem.querySelector('[data-line-item-price]') as HTMLDivElement;
    const priceTextElem = lineItemElem.querySelector(
      '[data-line-item-price-text]',
    ) as HTMLSpanElement | null;
    const comparePriceElem = lineItemElem.querySelector(
      '[data-line-item-compare]',
    ) as HTMLDivElement;
    const sideElem = lineItemElem.querySelector('.mini-cart__item-side') as HTMLElement;
    const mainElem = lineItemElem.querySelector('.mini-cart__item-main') as HTMLElement;

    const lineItemRoot = lineItemElem.querySelector('[data-line-item]') as HTMLElement | null;

    // Ensure originalPriceCents and compareAtPriceCents are set before continuing
    let originalPriceCents = -1,
      compareAtPriceCents = -1;

    // Which currency the compare amounts ended up in. The data attributes are
    // rendered by Liquid, whose money values are already presentment; only the
    // Storefront API fallback comes back in the shop's own currency and needs
    // converting.
    let compareAmountsAreShopCurrency = false;

    await (async () => {
      originalPriceCents = Number(lineItemRoot?.getAttribute('data-original-price') ?? -1) || -1;
      compareAtPriceCents = Number(lineItemRoot?.getAttribute('data-compare-at-price') ?? 0) || 0;

      if (originalPriceCents === -1) {
        this.logger.warn('Original price not found for line item', {
          lineItem,
          lineItemRoot,
        });

        const { data, errors }: { data: GetProductVariantsResponse; errors: any } = await graphql(
          this.getProductVariantsQuery,
          {
            id: enforceGraphQLID(lineItem.product_id.toString(), 'Product'),
          },
        );

        if (errors) {
          this.logger.error('Failed to get product variants', errors);
          return;
        }

        const variant = data.product.variants.nodes.find(
          (variant) => variant.id.toString().indexOf(lineItem.variant_id.toString()) !== -1,
        );

        if (!variant) {
          this.logger.error('Failed to get product variant', { lineItem, variant });
          return;
        }

        originalPriceCents = Number(variant.price.amount) * 100;
        compareAtPriceCents = Number(variant.compareAtPrice?.amount ?? '0') * 100;
        compareAmountsAreShopCurrency = true;
      }
    })();

    this.logger.log({ originalPriceCents, compareAtPriceCents });

    const formatPriceValue = (price: number): string => {
      if (price === 0 || Math.abs(price) < 0.01) {
        return 'FREE';
      }
      return this.formatPrice(price);
    };

    const replacePriceInText = (text: string, newPrice: number): string => {
      if (newPrice === 0 || Math.abs(newPrice) < 0.01) {
        return 'FREE';
      }
      const formattedPrice = formatPriceValue(newPrice);

      if (text.trim() === '') return formattedPrice;

      return text.replace(FIRST_SHOPIFY_MONEY_AMOUNT_REGEX, formattedPrice);
    };

    const priceTemplate = priceElem.getAttribute('data-template') ?? '';

    const baseText = priceTemplate.trim();
    const price = lineItem.final_price / 100;
    const compareBaseCents = compareAtPriceCents > 0 ? compareAtPriceCents : originalPriceCents;
    const comparePrice = compareBaseCents / 100;
    const totalPrice = price * lineItem.quantity;
    const totalComparePrice = comparePrice !== null ? comparePrice * lineItem.quantity : null;

    // Convert only what is actually in the shop's currency. Applying the rate
    // to a Liquid-rendered amount multiplied it a second time, so in every
    // market outside the shop's own currency an undiscounted line was struck
    // out as if it were on sale — most visibly on a Gift Card Pro custom
    // amount, where the line is one unit per dollar of card value.
    const rateRaw = parseFloat(String(window.Shopify?.currency?.rate ?? '1'));
    const currencyRate = !compareAmountsAreShopCurrency
      ? 1
      : Number.isFinite(rateRaw) && rateRaw > 0
        ? rateRaw
        : 1;
    const totalComparePriceDisplay =
      totalComparePrice !== null ? totalComparePrice * currencyRate : null;

    /** Nearest whole currency unit (validation + compare display). */
    const roundedCompareTotal =
      totalComparePriceDisplay !== null ? Math.round(totalComparePriceDisplay) : null;
    const roundedRegularTotal = Math.round(totalPrice);
    const compareTotalForValidation = this.isAudCurrency()
      ? totalComparePriceDisplay
      : roundedCompareTotal;
    const regularTotalForValidation = this.isAudCurrency() ? totalPrice : roundedRegularTotal;
    const compareTotalForDisplay = this.isAudCurrency()
      ? totalComparePriceDisplay
      : roundedCompareTotal;

    const isFreeProduct = Math.abs(totalPrice) < 0.01;

    // Only treat as on sale when "was" (rounded) is above current (rounded); hide if equal or lower.
    const hasValidComparePrice =
      comparePrice !== null &&
      !isNaN(comparePrice) &&
      compareTotalForValidation !== null &&
      compareTotalForValidation > regularTotalForValidation &&
      !(Math.abs(regularTotalForValidation) < 1 && Math.abs(compareTotalForValidation) < 1);

    const newPriceText = replacePriceInText(baseText, totalPrice);
    // Whole-unit rounded value; formatPrice / numberFormatter uses locale (e.g. 448 → 448,00 EUR).
    const newCompareText =
      hasValidComparePrice && compareTotalForDisplay !== null
        ? replacePriceInText(baseText, compareTotalForDisplay)
        : '';

    sideElem.classList.toggle('is-free-product', isFreeProduct);
    mainElem.classList.toggle('is-free-product', isFreeProduct);

    comparePriceElem.classList.toggle('is-visible', hasValidComparePrice);

    return {
      priceText: newPriceText,
      compareText: newCompareText,
      compareVisible: hasValidComparePrice,
      isFreeProduct,
      originalPriceCents,
      compareAtPriceCents,
    };
  }

  private setupQuantity(lineItemElem: HTMLElement, lineItem: CartLine) {
    if (!this.shouldShowQuantitySelectors(lineItem)) {
      const quantitySelectorRoot = lineItemElem.querySelector('.mini-cart__item-qty');
      if (quantitySelectorRoot) {
        quantitySelectorRoot.remove();
      }
    }
    const qtyElem = lineItemElem.querySelector('[data-quantity]') as HTMLDivElement;

    if (qtyElem) qtyElem.textContent = lineItem.quantity.toString();
  }

  private setupLineItemKey(lineItemElem: HTMLElement, lineItem: CartLine) {
    const lineItemKeyInput = lineItemElem.querySelector('[data-line-item-key]') as HTMLInputElement;

    if (!lineItemKeyInput) {
      this.logger.error('Failed to find data-line-item-key element');
      return;
    }

    lineItemKeyInput.value = lineItem.key ?? '';
  }

  private setupProductId(lineItemElem: HTMLElement, lineItem: CartLine) {
    const target = lineItemElem.querySelector('[data-product-id]');
    if (!target) {
      this.logger.error('Failed to find data-product-id element');
      return;
    }
    target.setAttribute('data-product-id', lineItem.product_id?.toString() ?? '');
  }

  private setupProductHandle(lineItemElem: HTMLElement, lineItem: CartLine) {
    const target = lineItemElem.querySelector('[data-product-handle]');
    if (!target) {
      this.logger.error('Failed to find data-product-handle element');
      return;
    }
    target.setAttribute('data-product-handle', lineItem.handle ?? '');
  }

  private setupVariantId(lineItemElem: HTMLElement, lineItem: CartLine) {
    const target = lineItemElem.querySelector('[data-variant-id]');
    if (!target) {
      this.logger.error('Failed to find data-variant-id element');
      return;
    }
    target.setAttribute('data-variant-id', lineItem.variant_id?.toString() ?? '');
  }

  private setupReviews(lineItemElem: HTMLElement, lineItem: CartLine) {
    const reviewsElem = lineItemElem.querySelector(
      '.stamped-product-reviews-badge',
    ) as HTMLSpanElement;
    reviewsElem.setAttribute('data-id', lineItem.product_id?.toString() ?? '');
    reviewsElem.setAttribute('data-product-sku', lineItem.handle ?? '');
    reviewsElem.setAttribute('data-product-title', lineItem.product_title ?? '');
    reviewsElem.setAttribute('data-product-type', lineItem.product_type ?? '');
  }

  private setupReviewsCache(lineItemElem: HTMLElement, lineItem: CartLine) {
    const reviewsWrapper = lineItemElem.querySelector(
      '.mini-cart__item-reviews-wrapper',
    ) as HTMLElement;
    const cachedReviews = this.reviewsCache.get(lineItem.product_id.toString());
    if (cachedReviews) reviewsWrapper.innerHTML = cachedReviews;
  }

  private generateLineItem(lineItem: CartLine) {
    const lineItemElem = this.lineItemTemplate!.content.cloneNode(true) as HTMLElement;

    this.setupImage(lineItemElem, lineItem);
    this.setupLinks(lineItemElem, lineItem);

    const titleElem = this.setupTitle(lineItemElem, lineItem);
    const propertiesElem = this.setupProperties(lineItemElem, lineItem);
    this.setupVariantOptions(lineItem, propertiesElem, titleElem);

    this.applyCachedPriceText(lineItemElem, lineItem.key);
    this.setupQuantity(lineItemElem, lineItem);
    this.setupLineItemKey(lineItemElem, lineItem);
    this.setupProductId(lineItemElem, lineItem);
    this.setupProductHandle(lineItemElem, lineItem);
    this.setupVariantId(lineItemElem, lineItem);

    this.setupReviewsCache(lineItemElem, lineItem);
    if (!this.reviewsCache.has(lineItem.product_id.toString()))
      this.setupReviews(lineItemElem, lineItem);

    return lineItemElem;
  }

  private async renderLineItems(items: CartLine[]) {
    this.capturePriceTextCacheFromExistingDom();

    this.itemsListElem!.querySelectorAll('[data-line-item]').forEach((lineItemElem) => {
      const reviewsWrapper = lineItemElem.querySelector('.mini-cart__item-reviews-wrapper');

      const badge = reviewsWrapper?.querySelector('.stamped-product-reviews-badge');
      const productId = badge?.getAttribute('data-id');

      if (productId && reviewsWrapper && reviewsWrapper.innerHTML.trim() !== '') {
        this.reviewsCache.set(productId, reviewsWrapper.innerHTML);
      }

      const lineKey = (
        lineItemElem.querySelector('[data-line-item-key]') as HTMLInputElement | null
      )?.value;
      if (!lineKey) return;

      const originalPrice = Number(lineItemElem.getAttribute('data-original-price') ?? 0) || -1;
      const compareAtPrice = Number(lineItemElem.getAttribute('data-compare-at-price') ?? 0) || -1;
      this.priceAttributeCache.set(lineKey, {
        originalPrice,
        compareAtPrice,
      });
    });

    this.itemsListElem!.innerHTML = '';

    const pricingWork: Array<Promise<void>> = [];

    items.forEach((item) => {
      if (item.properties && item.properties.__free_gift === 'true') return;

      const lineItemFragment = this.generateLineItem(item);
      const lineItemRoot = (lineItemFragment as any).querySelector?.('[data-line-item]') as
        | HTMLElement
        | undefined;
      const cachedPrices = item.key ? this.priceAttributeCache.get(item.key) : undefined;
      if (lineItemRoot && cachedPrices) {
        lineItemRoot.setAttribute('data-original-price', String(cachedPrices.originalPrice ?? 0));
        lineItemRoot.setAttribute(
          'data-compare-at-price',
          String(cachedPrices.compareAtPrice ?? 0),
        );
      }

      this.itemsListElem!.appendChild(lineItemFragment);

      const appendedRoot = this.itemsListElem!.querySelector(
        `[data-line-item] [data-line-item-key][value="${CSS.escape(item.key ?? '')}"]`,
      )?.closest('[data-line-item]') as HTMLElement | null;
      const appendedLineItemElem = appendedRoot?.parentElement as HTMLElement | null;
      const pricingTarget = appendedLineItemElem ?? (lineItemRoot as any);

      if (pricingTarget) {
        const work = this.computePricing(pricingTarget, item)
          .then((computed) => {
            this.priceTextCache.set(item.key ?? '', {
              priceText: computed.priceText,
              compareText: computed.compareText,
              compareVisible: computed.compareVisible,
              isFreeProduct: computed.isFreeProduct,
            });

            if (item.key) {
              this.priceAttributeCache.set(item.key, {
                originalPrice: computed.originalPriceCents,
                compareAtPrice: computed.compareAtPriceCents,
              });
            }
          })
          .catch((e) => {
            this.logger.error('Failed to compute pricing', e);
          });
        pricingWork.push(work);
      }
    });

    if (pricingWork.length > 0) {
      await Promise.all(pricingWork);
      requestAnimationFrame(() => {
        this.itemsListElem!.querySelectorAll<HTMLElement>('[data-line-item]').forEach(
          (lineItemElem) => {
            const lineKey = (
              lineItemElem.querySelector('[data-line-item-key]') as HTMLInputElement | null
            )?.value;
            if (!lineKey) return;
            this.applyCachedPriceText(lineItemElem, lineKey);
          },
        );
      });
    }

    this.attachEventListeners();

    this.setupStampedReviews();
  }

  private setupStampedReviews() {
    const self = this;

    if ((window as any).StampedFn) {
      (window as any).StampedFn.loadBadges();
    } else {
      setTimeout(() => {
        self.setupStampedReviews();
      }, 1000);
    }
  }

  private attachEventListeners() {
    this.itemsListElem!.querySelectorAll('[data-line-item]').forEach((lineItemElem) => {
      // Called from both hydrate() and renderLineItems(). A render that reuses
      // existing nodes would otherwise stack a second set of handlers on them,
      // and every click would fire once per binding.
      if (this.boundLineItems.has(lineItemElem)) return;
      this.boundLineItems.add(lineItemElem);

      const removeBtn = lineItemElem.querySelector('[data-remove]') as HTMLButtonElement;

      if (removeBtn) {
        removeBtn.addEventListener('click', this.onRemoveClick);
      }

      const decrementBtn = lineItemElem.querySelector('[data-decrement]') as HTMLButtonElement;

      if (decrementBtn) {
        decrementBtn.addEventListener('click', this.onDecrementClick);
      }

      const incrementBtn = lineItemElem.querySelector('[data-increment]') as HTMLButtonElement;
      if (incrementBtn) {
        incrementBtn.addEventListener('click', this.onIncrementClick);
      }
    });
  }

  private readLineItemKeyFromEvent(e: MouseEvent): string | null {
    const input = (e.target as HTMLElement)
      .closest('[data-line-item]')
      ?.querySelector('[data-line-item-key]') as HTMLInputElement | null;
    const key = input?.value?.trim();
    return key ? key : null;
  }

  private readLineItemElementFromEvent(e: MouseEvent): Element | null {
    return (e.target as HTMLElement).closest('[data-line-item]');
  }

  private isShippingProtectionLineItem(lineItemElem: Element | null | undefined): boolean {
    return lineItemElem?.getAttribute('data-product-handle') === 'shipping-protection';
  }

  private markShippingProtectionRemovedIfNeeded(e: MouseEvent) {
    if (this.isShippingProtectionLineItem(this.readLineItemElementFromEvent(e))) {
      sessionStorage.setItem('shipping-protection-removed', 'true');
    }
  }

  // Stale DOM key (cart mutated since render). Refetch + emit Changed to
  // force a re-render with the current line item keys before the user retries.
  private forceCartRefresh(parentMiniCart: MiniCart) {
    void parentMiniCart.cartApi
      .get()
      .then((cart) => {
        parentMiniCart.bus.emit(CartEvents.Changed, { reason: 'change', cart });
      })
      .catch((err) => {
        this.logger.error('Failed to refresh cart after stale line item click', err);
      });
  }

  private runLineItemAction(
    e: MouseEvent,
    action: (parentMiniCart: MiniCart, key: string) => Promise<unknown>,
    actionName: string,
  ) {
    const parentMiniCart = this.closest('mini-cart')! as MiniCart;

    if (!parentMiniCart || this.classList.contains('is-loading')) return;

    const key = this.readLineItemKeyFromEvent(e);
    if (!key) {
      this.logger.error(`${actionName}: missing line item key, skipping and refreshing cart`);
      this.forceCartRefresh(parentMiniCart);
      return;
    }

    const miniCartFooter = parentMiniCart.querySelector('.mini-cart__footer') as HTMLElement;
    miniCartFooter.classList.add('is-loading');

    const self = this;
    self.classList.add('is-loading');

    const clearLoading = () => {
      self.classList.remove('is-loading');
      setTimeout(() => {
        miniCartFooter.classList.remove('is-loading');
      }, 1000);
    };

    action(parentMiniCart, key)
      .then(clearLoading)
      .catch((err) => {
        clearLoading();
        if (err instanceof StaleLineItemKeyError || err instanceof MissingLineItemKeyError) {
          this.logger.warn(`${actionName}: ${err.message}`);
          return;
        }
        console.error(err);
      });
  }

  private onRemoveClick = (e: MouseEvent) => {
    this.markShippingProtectionRemovedIfNeeded(e);

    this.runLineItemAction(
      e,
      (parentMiniCart, key) => parentMiniCart.cartApi.removeByKey(key),
      'Remove',
    );
  };

  private onDecrementClick = (e: MouseEvent) => {
    this.queueQuantityDelta(e, -1, 'Decrement');
  };

  private onIncrementClick = (e: MouseEvent) => {
    this.queueQuantityDelta(e, 1, 'Increment');
  };

  /**
   * Record a quantity click, update the displayed quantity straight away, and
   * schedule a flush.
   *
   * Deliberately not gated on `is-loading`: dropping clicks while a request is
   * in flight makes the button look dead, which is what drives users to spam it
   * in the first place. Clicks accumulate instead and settle as one request.
   *
   * @param e Originating click.
   * @param delta Quantity change to accumulate.
   * @param actionName Label used in logs.
   */
  private queueQuantityDelta(e: MouseEvent, delta: number, actionName: string) {
    const parentMiniCart = this.closest('mini-cart') as MiniCart | null;
    if (!parentMiniCart) return;

    const key = this.readLineItemKeyFromEvent(e);
    if (!key) {
      this.logger.error(`${actionName}: missing line item key, skipping and refreshing cart`);
      this.forceCartRefresh(parentMiniCart);
      return;
    }

    const lineItemElem = this.readLineItemElementFromEvent(e);
    const qtyElem = lineItemElem?.querySelector('[data-quantity]') as HTMLElement | null;
    const displayed = Number(qtyElem?.textContent?.trim());
    if (
      delta < 0 &&
      this.isShippingProtectionLineItem(lineItemElem) &&
      Number.isFinite(displayed) &&
      displayed + delta <= 0
    ) {
      sessionStorage.setItem('shipping-protection-removed', 'true');
    }
    if (qtyElem && Number.isFinite(displayed)) {
      qtyElem.textContent = String(Math.max(0, displayed + delta));
    }

    this.pendingQuantityDeltas.set(key, (this.pendingQuantityDeltas.get(key) ?? 0) + delta);
    this.scheduleQuantityFlush(parentMiniCart);
  }

  private scheduleQuantityFlush(parentMiniCart: MiniCart) {
    if (this.pendingQuantityFlushTimer) clearTimeout(this.pendingQuantityFlushTimer);

    this.pendingQuantityFlushTimer = setTimeout(() => {
      this.pendingQuantityFlushTimer = null;
      void this.flushQuantityDeltas(parentMiniCart);
    }, MiniCartLineItemsComponent.QUANTITY_FLUSH_DELAY_MS);
  }

  /**
   * Send the accumulated deltas, one request per affected line.
   *
   * Deltas are applied against the quantity read inside the mutation queue, so
   * the request carries "two fewer" rather than an absolute quantity computed
   * from a cart that may already be out of date.
   */
  private async flushQuantityDeltas(parentMiniCart: MiniCart) {
    const deltas = Array.from(this.pendingQuantityDeltas.entries());
    this.pendingQuantityDeltas.clear();
    if (deltas.length === 0) return;

    const miniCartFooter = parentMiniCart.querySelector('.mini-cart__footer') as HTMLElement | null;
    miniCartFooter?.classList.add('is-loading');
    this.classList.add('is-loading');

    try {
      for (const [key, delta] of deltas) {
        if (delta === 0) continue;

        try {
          await parentMiniCart.cartApi.incrementByKey(key, delta);
        } catch (err) {
          if (err instanceof StaleLineItemKeyError || err instanceof MissingLineItemKeyError) {
            this.logger.warn(`Quantity flush: ${(err as Error).message}`);
            continue;
          }
          console.error(err);
        }
      }
    } finally {
      this.classList.remove('is-loading');
      setTimeout(() => {
        miniCartFooter?.classList.remove('is-loading');
      }, 1000);
    }
  }

  /**
   * Re-apply not-yet-sent deltas after a render.
   *
   * A render driven by a poll or another app writes the server quantity back
   * into the DOM, which would otherwise make the user's clicks appear to be
   * undone until the flush lands.
   */
  private applyPendingDeltasToDom() {
    if (this.pendingQuantityDeltas.size === 0) return;

    this.itemsListElem?.querySelectorAll('[data-line-item]').forEach((lineItemElem) => {
      const key = (lineItemElem.querySelector('[data-line-item-key]') as HTMLInputElement | null)
        ?.value;
      if (!key) return;

      const delta = this.pendingQuantityDeltas.get(key) ?? this.pendingDeltaForVariantOf(key);
      if (!delta) return;

      const qtyElem = lineItemElem.querySelector('[data-quantity]') as HTMLElement | null;
      const current = Number(qtyElem?.textContent?.trim());
      if (qtyElem && Number.isFinite(current)) {
        qtyElem.textContent = String(Math.max(0, current + delta));
      }
    });
  }

  /**
   * Sum pending deltas recorded against any key for the same variant.
   *
   * Covers the case where the line was rekeyed by a property write between the
   * click and the render.
   */
  private pendingDeltaForVariantOf(key: string): number {
    const variantId = key.split(':')[0];
    let total = 0;

    for (const [pendingKey, delta] of this.pendingQuantityDeltas.entries()) {
      if (pendingKey.split(':')[0] === variantId) total += delta;
    }

    return total;
  }

  private onCartChanged(e: TypedCartEvent<'cart:changed:debounced'>) {
    const { cart } = e.detail;
    this.logger.debug('Cart changed', cart);
    void this.render(cart);
  }

  private async render(cart: Cart) {
    if (!this.emptyMessageElem) return;
    this.emptyMessageElem!.style.display = cart.item_count === 0 ? 'block' : 'none';

    await this.renderLineItems(cart.items);

    this.applyPendingDeltasToDom();

    void this.runLineItemPropertiesUpdateChain(cart);
  }

  private bindEvents() {
    this.eventBus!.on(CartEvents.DebouncedChange, this.onCartChanged.bind(this));
    this.eventBus!.on(CartEvents.AddSuccess, this.onCartChanged.bind(this));

    this.eventBus!.on(CartEvents.UIOpened, this.onMiniCartUIOpened);
    this.eventBus!.on(CartEvents.UIClosed, this.onMiniCartUIClosed);
  }

  private unbindEvents() {
    this.eventBus!.off(CartEvents.DebouncedChange, this.onCartChanged.bind(this));
    this.eventBus!.off(CartEvents.AddSuccess, this.onCartChanged.bind(this));

    this.eventBus!.off(CartEvents.UIOpened, this.onMiniCartUIOpened);
    this.eventBus!.off(CartEvents.UIClosed, this.onMiniCartUIClosed);
  }

  private onMiniCartReady(parentMiniCart: MiniCart) {
    this.eventBus = parentMiniCart.bus;
    this.parentMiniCart = parentMiniCart;

    this.hydrate();

    this.bindEvents();
  }

  // Cart items are loaded in by Liquid, so they need to by hydrated with JS functionality
  private hydrate() {
    this.attachEventListeners();

    this.setupStampedReviews();
  }

  connectedCallback() {
    this.abortController = new AbortController();

    const parentMiniCart = this.closest('mini-cart')! as MiniCart;
    if (!parentMiniCart) {
      this.logger.error('MiniCartHeader must be a child of a MiniCart element');
      throw new Error('MiniCartHeader must be a child of a MiniCart element');
    }

    this.lineItemTemplate = this.querySelector(
      '[data-line-item-template]',
    ) as HTMLTemplateElement | null;
    this.emptyMessageElem = this.querySelector('.mini-cart__empty-message') as HTMLElement | null;
    this.itemsListElem = this.querySelector('.mini-cart__items-list') as HTMLElement | null;

    this.initLineItemPropertyCssRules();

    this.initPriceAttributeCacheFromDom();

    parentMiniCart.execOnReady(this.onMiniCartReady.bind(this, parentMiniCart));
  }

  private initPriceAttributeCacheFromDom() {
    if (!this.itemsListElem) return;

    this.itemsListElem.querySelectorAll('[data-line-item]').forEach((lineItemElem) => {
      const lineKey = (
        lineItemElem.querySelector('[data-line-item-key]') as HTMLInputElement | null
      )?.value;
      if (!lineKey) return;

      const originalPrice = Number(lineItemElem.getAttribute('data-original-price') ?? 0) || -1;
      const compareAtPrice = Number(lineItemElem.getAttribute('data-compare-at-price') ?? 0) || -1;

      this.priceAttributeCache.set(lineKey, {
        originalPrice,
        compareAtPrice,
      });

      const priceElem = lineItemElem.querySelector(
        '[data-line-item-price]',
      ) as HTMLDivElement | null;
      const priceTextElem = lineItemElem.querySelector(
        '[data-line-item-price-text]',
      ) as HTMLSpanElement | null;
      const comparePriceElem = lineItemElem.querySelector(
        '[data-line-item-compare]',
      ) as HTMLDivElement | null;
      const sideElem = lineItemElem.querySelector('.mini-cart__item-side') as HTMLElement | null;

      this.priceTextCache.set(lineKey, {
        priceText: (priceTextElem?.textContent ?? priceElem?.textContent ?? '').trim(),
        compareText: comparePriceElem?.textContent?.trim() ?? '',
        compareVisible: comparePriceElem?.classList.contains('is-visible') ?? false,
        isFreeProduct: sideElem?.classList.contains('is-free-product') ?? false,
      });
    });
  }

  disconnectedCallback() {
    this.abortController?.abort();

    this.unbindEvents();
  }
}

if (!customElements.get('mini-cart-line-items')) {
  customElements.define('mini-cart-line-items', MiniCartLineItemsComponent);
}
